import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Avatar,
    Button,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    Divider,
    Skeleton
} from '@mui/material';
import { ArrowBack, PersonAdd, PersonRemove, People, Person } from '@mui/icons-material';
import { socialAPI, toggleFollow } from '../utils/api';

const UserProfile = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [followingAction, setFollowingAction] = useState(false);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await socialAPI.getUserProfile(userId);
            setProfile(response.data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchProfile();
        }
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFollow = async () => {
        if (followingAction) return;

        try {
            setFollowingAction(true);
            setError(null);
            const result = await toggleFollow(userId);
            
            setProfile(prev => ({
                ...prev,
                relationship: {
                    ...prev.relationship,
                    is_following: result.following
                },
                stats: {
                    ...prev.stats,
                    followers_count: prev.stats.followers_count + (result.following ? 1 : -1)
                }
            }));
        } catch (err) {
            console.error('Error toggling follow:', err);
            setError('Failed to update follow status. Please try again.');
        } finally {
            setFollowingAction(false);
        }
    };

    const getFollowButtonProps = () => {
        if (!profile || profile.relationship.is_current_user) {
            return null;
        }

        if (profile.relationship.is_following) {
            return {
                variant: 'outlined',
                startIcon: <PersonRemove />,
                children: 'Unfollow'
            };
        }

        return {
            variant: 'contained',
            startIcon: <PersonAdd />,
            children: profile.relationship.is_follower ? 'Follow Back' : 'Follow'
        };
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Skeleton variant="circular" width={84} height={84} />
                        <Box flex={1}>
                            <Skeleton variant="text" width="38%" height={36} />
                            <Skeleton variant="text" width="24%" />
                        </Box>
                    </Box>
                    <Divider sx={{ my: 3 }} />
                    <Box display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={2}>
                        <Skeleton variant="rounded" height={58} />
                        <Skeleton variant="rounded" height={58} />
                        <Skeleton variant="rounded" height={58} />
                    </Box>
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Alert severity="error">
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!profile) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
                <Alert severity="warning">
                    User not found
                </Alert>
            </Container>
        );
    }

    const buttonProps = getFollowButtonProps();

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    borderRadius: 3,
                    background:
                        'radial-gradient(220px 140px at 90% -30%, rgba(255, 110, 77, 0.18), transparent 70%), linear-gradient(135deg, rgba(14, 123, 255, 0.12), rgba(0, 165, 255, 0.07))',
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton aria-label="Back" onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 800 }}>
                        {profile.user.username}
                    </Typography>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 }, mb: 3, borderRadius: 3 }}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Avatar 
                            src={profile.user.avatar_url} 
                            alt={profile.user.username}
                            sx={{ width: 80, height: 80, mr: 3 }}
                        >
                            {!profile.user.avatar_url && profile.user.username?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box flex={1}>
                            <Typography variant="h5" component="h1" gutterBottom>
                                {profile.user.username}
                            </Typography>
                            {profile.user.name && (
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    {profile.user.name}
                                </Typography>
                            )}
                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                {profile.relationship.is_mutual && (
                                    <Chip 
                                        label="Mutual Connection" 
                                        size="small" 
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {profile.relationship.is_follower && !profile.relationship.is_following && (
                                    <Chip 
                                        label="Follows You" 
                                        size="small" 
                                        color="secondary"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                        {buttonProps && (
                            <Button
                                {...buttonProps}
                                disabled={followingAction}
                                onClick={handleFollow}
                                sx={{ ml: 2, minWidth: 116 }}
                            >
                                {followingAction ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    buttonProps.children
                                )}
                            </Button>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={3}>
                        <Grid item xs={4}>
                            <Box textAlign="center">
                                <Typography variant="h6" component="div">
                                    {profile.stats.posts_count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Posts
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box 
                                textAlign="center" 
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    p: 1,
                                    transition: 'background-color 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(14, 123, 255, 0.08)',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                                onClick={() => navigate(`/users/${userId}/followers`)}
                            >
                                <Typography variant="h6" component="div">
                                    {profile.stats.followers_count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Followers
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box 
                                textAlign="center" 
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    p: 1,
                                    transition: 'background-color 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(14, 123, 255, 0.08)',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                                onClick={() => navigate(`/users/${userId}/following`)}
                            >
                                <Typography variant="h6" component="div">
                                    {profile.stats.following_count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Following
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
            </Paper>

            {profile.relationship.is_current_user && (
                <Box display="flex" gap={2} mb={3}>
                    <Button
                        variant="outlined"
                        startIcon={<People />}
                        onClick={() => navigate(`/users/${userId}/followers`)}
                        fullWidth
                    >
                        View Followers
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Person />}
                        onClick={() => navigate(`/users/${userId}/following`)}
                        fullWidth
                    >
                        View Following
                    </Button>
                </Box>
            )}

            <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Member Since
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {new Date(profile.user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}
                </Typography>
            </Paper>
        </Container>
    );
};

export default UserProfile;
